import { useMemo } from 'react';
import { ace_hooks, assignment_hooks } from '@axo/shared/data-access/hooks';
import { useShouldUseAssignmentsVersion } from './useShouldUseAssignmentsVersion';

type UseAssignmentsParams = {
  applicationID: string;
  marketCountry: string;
};

type UseAssignmentsResult = {
  assignee: string | undefined;
  assignmentType: string | undefined;
  processedAt: string | undefined;
  isLoading: boolean;
  isSuccess: boolean;
};

export const useAssignments = ({
  applicationID,
  marketCountry,
}: UseAssignmentsParams): UseAssignmentsResult => {
  const version = useShouldUseAssignmentsVersion({
    applicationID,
    marketCountry,
  });

  const v3Application = ace_hooks.useGetAssignmentApplicationV3(applicationID, {
    enabled: version === 'v3',
  });

  const buckets = ace_hooks.useApplicationBucket(applicationID, {
    enabled: version === 'buckets',
  });

  const assignment = assignment_hooks.useAssignment(
    marketCountry,
    applicationID,
    { enabled: version === 'legacy' }
  );

  return useMemo(() => {
    switch (version) {
      case 'v3':
        return {
          assignee: v3Application.data?.AssignedAgentID ?? undefined,
          assignmentType: v3Application.data?.AssignmentType ?? undefined,
          // V3 uses empty string as "unprocessed" sentinel
          processedAt: v3Application.data?.ProcessedAt || undefined,
          isLoading: v3Application.isLoading,
          isSuccess: v3Application.isSuccess,
        };
      case 'buckets':
        return {
          assignee: buckets.data?.AssigneeID ?? undefined,
          assignmentType: undefined,
          processedAt: buckets.data?.ProcessedAt ?? undefined,
          isLoading: buckets.isLoading,
          isSuccess: buckets.isSuccess,
        };
      default:
        return {
          assignee: assignment.data?.User ?? undefined,
          assignmentType: undefined,
          processedAt: assignment.data?.ProcessedAt ?? undefined,
          isLoading: assignment.isLoading,
          isSuccess: assignment.isSuccess,
        };
    }
  }, [
    version,
    v3Application.data,
    v3Application.isLoading,
    v3Application.isSuccess,
    buckets.data,
    buckets.isLoading,
    buckets.isSuccess,
    assignment.data,
    assignment.isLoading,
    assignment.isSuccess,
  ]);
};
